import {
  makeObservable,
  action,
  observable,
  IReactionDisposer,
  reaction,
} from 'mobx';

import { ILocalStore } from 'stores/interfaces';

import { ConnectorOptions, ConnectorRawValue, ConnectorValue } from '../types';

const STORAGE_EVENT_KEY = 'storage';

type PrivateFields = '_value' | '_setValue';

/**
 * Данный коннектор создает состояние привязанное к localStorage
 */
export class LSConnector<V extends ConnectorValue> implements ILocalStore {
  constructor(options: ConnectorOptions<V>) {
    makeObservable<LSConnector<V>, PrivateFields>(this, {
      _value: observable.ref,
      _setValue: action.bound,
    });

    this._options = options;

    /** Умышленно не сетим в ls начальное значение.
     * кейс: захотим поменять изначальное состояние со временем,
     * а у пользователя подтянется старое значение, даже если он не сохранял.
     */
    this._value = this._parsedLSValue;

    this._prevRawValue = this._rawValue;

    this._disposers.push(
      reaction(
        () => this._value,
        (nextValue) => options.onChange?.(nextValue)
      )
    );
    this._disposers.push(
      reaction(options.watchValue, (value) => this._setValue(value))
    );

    // Если значение в Localstorage при инициализации не совпадает со значением по умолчанию, то меняем отслеживаемое значение.
    if (options.defaultValue !== this._parsedLSValue) {
      options.onChange(this._parsedLSValue);
    }

    window.addEventListener(STORAGE_EVENT_KEY, this._handleStorage);
  }

  private _value: V = null as V;
  private _prevRawValue: ConnectorRawValue;

  private readonly _options: ConnectorOptions<V>;

  private readonly _disposers: IReactionDisposer[] = [];

  private get _rawValue(): ConnectorRawValue {
    return window.localStorage.getItem(this._options.key);
  }

  get value(): V {
    return this._value;
  }

  private get _parsedLSValue(): V {
    const { defaultValue, normalizeFromStorage } = this._options;

    try {
      return this._rawValue === null
        ? defaultValue
        : normalizeFromStorage(this._rawValue);
    } catch (e) {
      return defaultValue;
    }
  }

  private _setValue(nextValue: V): void {
    if (nextValue === this._value) {
      return;
    }

    this._prevRawValue = this._rawValue;
    this._value = nextValue;
    window.localStorage.setItem(
      this._options.key,
      this._options.normalizeToStorage(nextValue)
    );
  }

  /**
   * Добавляем слушатель события для отлавливания изменений
   * Из активных вкладок данного хоста
   * Внимание: отслеживает изменения ТОЛЬКО в других активных вкладках
   * */
  private _handleStorage = (e: StorageEvent): void => {
    // игнорируем изменения других ключей и изменения на такие же значения
    if (e.key !== this._options.key || e.newValue === this._prevRawValue) {
      return;
    } else if (e.key === null) {
      // при удалении поля возвращаем ему дефолтное значение
      this._setValue(this._options.defaultValue);
    } else {
      this._setValue(this._options.normalizeFromStorage(e.newValue));
    }
  };

  destroy(): void {
    /* отписываем от слушателя */
    window.removeEventListener(STORAGE_EVENT_KEY, this._handleStorage);
    this._disposers.forEach((reaction) => reaction());
  }
}

export default LSConnector;
